import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import {
  HOLD_BULK_FIELDS,
  HOLD_BULK_GROUPS,
  HOLD_BULK_TABLE,
  HOLD_BULK_VERSION_LABEL,
  formatHoldDisplayValue,
  getHoldFieldLabel,
  getHoldFieldMeta,
} from "./holdBulkFieldConfig";

const LIST_LIMIT_DEFAULT = 100;
const LIST_LIMIT_MAX = 500;

const emptyFilter = () => ({ column: "uid", value: "" });
const emptyUpdate = () => ({ column: "", value: "" });

function FieldSelect({ value, onChange, placeholder, allowedColumns }) {
  const grouped = useMemo(() => {
    const map = new Map();
    HOLD_BULK_FIELDS.forEach((field) => {
      if (allowedColumns && !allowedColumns.has(field.column)) {
        return;
      }
      if (!map.has(field.group)) {
        map.set(field.group, []);
      }
      map.get(field.group).push(field);
    });
    return map;
  }, [allowedColumns]);

  return (
    <select className="admin-bulk__select" value={value} onChange={onChange}>
      <option value="">{placeholder || "항목 선택"}</option>
      {HOLD_BULK_GROUPS.map((group) => {
        const fields = grouped.get(group);
        if (!fields?.length) {
          return null;
        }
        return (
          <optgroup key={group} label={group}>
            {fields.map((field) => (
              <option key={field.column} value={field.column}>
                {field.label}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

function ValueEditor({ column, value, onChange, disabled, mode = "set" }) {
  const field = getHoldFieldMeta(column);
  const isFilter = mode === "filter";

  if (!column || disabled) {
    return (
      <input
        className="admin-bulk__input"
        type="text"
        value=""
        placeholder="값 입력"
        disabled
      />
    );
  }

  if (field.inputType === "escape_px") {
    return (
      <input
        className="admin-bulk__input"
        type="text"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        placeholder={isFilter ? "가격 (0 = 초기화)" : "가격 입력 (0 = 초기화)"}
        disabled={disabled}
      />
    );
  }

  if (field.options) {
    return (
      <select className="admin-bulk__select" value={value} onChange={onChange} disabled={disabled}>
        <option value="">{isFilter ? "값 선택" : "값 선택"}</option>
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      className="admin-bulk__input"
      type={field.inputType === "number" ? "number" : "text"}
      value={value}
      onChange={onChange}
      placeholder={isFilter ? "값 입력" : field.inputType === "number" ? "숫자" : "값 입력 (빈 값 = NULL)"}
      disabled={disabled}
    />
  );
}

export default function AdminCoinsDataBulk() {
  const [cookies] = useCookies(["uuid"]);
  const [allowedColumns, setAllowedColumns] = useState(null);
  const [filters, setFilters] = useState([emptyFilter()]);
  const [updates, setUpdates] = useState([emptyUpdate()]);
  const [listLimit, setListLimit] = useState(LIST_LIMIT_DEFAULT);
  const [matchedCount, setMatchedCount] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [listTruncated, setListTruncated] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [listLoading, setListLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const readUuidFromDocumentCookie = () => {
    const match = document.cookie.match(/(?:^|;\s*)uuid=([^;]*)/);
    return match ? decodeURIComponent(match[1].trim()) : "";
  };

  const ensureAdminSession = useCallback(async () => {
    const uuid = cookies.uuid || readUuidFromDocumentCookie();
    if (!uuid) {
      alert("로그인 정보가 없습니다. 관리자 로그인 페이지로 이동합니다.");
      window.location.href = "/AdminLogin";
      return false;
    }

    const response = await fetch(`/SetPage/admin_user?uuid=${encodeURIComponent(uuid)}`);
    if (!response.ok) {
      alert("관리자 세션 확인에 실패했습니다.");
      window.location.href = "/AdminLogin";
      return false;
    }

    const data = await response.json();
    if (!Array.isArray(data) || !data[0]) {
      alert("슈퍼 관리자 권한이 없습니다.");
      window.location.href = "/AdminLogin";
      return false;
    }

    return true;
  }, [cookies.uuid]);

  const loadSchema = useCallback(async () => {
    setErrorMessage("");
    try {
      const response = await fetch("/admin/hold-bulk/columns");
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.message || "홀드버전 컬럼 정보를 불러오지 못했습니다.");
      }
      setAllowedColumns(new Set(result.columns || []));
    } catch (error) {
      setAllowedColumns(null);
      setErrorMessage(error.message || "컬럼 정보 조회 실패");
    }
  }, []);

  useEffect(() => {
    ensureAdminSession().then((ok) => {
      if (ok) {
        loadSchema();
      }
    });
  }, [ensureAdminSession, loadSchema]);

  const resetListState = () => {
    setMatchedCount(null);
    setLoadedCount(0);
    setListTruncated(false);
    setPreviewRows([]);
    setSelectedRowIds(new Set());
  };

  const updateFilterRow = (index, field, value) => {
    resetListState();
    setFilters((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }
        const next = { ...row, [field]: value };
        if (field === "column") {
          next.value = "";
        }
        return next;
      })
    );
  };

  const updateSetRow = (index, field, value) => {
    setUpdates((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }
        const next = { ...row, [field]: value };
        if (field === "column") {
          next.value = "";
        }
        return next;
      })
    );
  };

  const buildPayload = (limitOverride) => ({
    filters: filters
      .filter((row) => row.column && String(row.value ?? "").trim() !== "")
      .map((row) => ({
        column: row.column,
        operator: "=",
        value: row.value,
      })),
    updates: updates
      .filter((row) => row.column)
      .map((row) => ({
        column: row.column,
        value: row.value,
      })),
    limit: limitOverride ?? listLimit,
  });

  const fetchList = async (limitOverride) => {
    const payload = buildPayload(limitOverride);
    if (!payload.filters.length) {
      alert("조건 항목과 값을 1개 이상 입력해주세요.");
      return null;
    }

    setListLoading(true);
    setErrorMessage("");
    try {
      const response = await fetch("/admin/hold-bulk/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.message || "목록 조회에 실패했습니다.");
      }

      const rows = Array.isArray(result.rows) ? result.rows : [];
      setMatchedCount(result.matchedCount ?? 0);
      setLoadedCount(result.loadedCount ?? rows.length);
      setListTruncated(Boolean(result.truncated));
      setPreviewRows(rows);
      setSelectedRowIds(new Set());
      if (limitOverride) {
        setListLimit(limitOverride);
      }
      return rows;
    } catch (error) {
      resetListState();
      setErrorMessage(error.message || "목록 조회 실패");
      return null;
    } finally {
      setListLoading(false);
    }
  };

  const handleLoadList = () => fetchList();

  const handleLoadAllMatched = () => {
    if (!matchedCount || matchedCount <= loadedCount) {
      return;
    }
    const nextLimit = Math.min(matchedCount, LIST_LIMIT_MAX);
    fetchList(nextLimit);
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const selectAllLoadedRows = () => {
    setSelectedRowIds(new Set(previewRows.map((row) => Number(row.id)).filter(Boolean)));
  };

  const clearRowSelection = () => {
    setSelectedRowIds(new Set());
  };

  const handleApply = async () => {
    const payload = buildPayload();
    if (!payload.filters.length) {
      alert("조건 항목과 값을 1개 이상 입력해주세요.");
      return;
    }
    if (!payload.updates.length) {
      alert("수정할 항목(SET)을 1개 이상 입력해주세요.");
      return;
    }
    if (previewRows.length === 0) {
      alert("먼저 조건에 맞는 목록을 조회해주세요.");
      return;
    }
    if (selectedRowIds.size === 0) {
      alert("수정할 행을 체크박스로 1개 이상 선택해주세요.");
      return;
    }

    for (const row of payload.updates) {
      const meta = getHoldFieldMeta(row.column);
      if (meta.inputType !== "escape_px" || String(row.value ?? "").trim() === "") {
        continue;
      }
      const trimmed = String(row.value).trim().replace(/,/g, "");
      const px = Number(trimmed);
      if (!Number.isFinite(px) || px < 0) {
        alert(`${getHoldFieldLabel(row.column)}: 0 이상의 유효한 가격을 입력해주세요.`);
        return;
      }
    }

    const selectedIds = [...selectedRowIds];
    const updateSummary = payload.updates
      .map(
        (row) =>
          `${getHoldFieldLabel(row.column)} → ${row.value === "" ? "NULL" : formatHoldDisplayValue(row.column, row.value)}`
      )
      .join("\n");

    const confirmed = window.confirm(
      [
        `버전: ${HOLD_BULK_VERSION_LABEL}`,
        `테이블: ${HOLD_BULK_TABLE}`,
        `선택 수정: ${selectedIds.length}건`,
        "",
        "수정 내용:",
        updateSummary,
        "",
        "선택한 행만 수정하시겠습니까?",
      ].join("\n")
    );

    if (!confirmed) {
      return;
    }

    setApplyLoading(true);
    setErrorMessage("");
    try {
      const response = await fetch("/admin/hold-bulk/bulk-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: payload.filters,
          updates: payload.updates,
          selectedIds,
          expectedCount: selectedIds.length,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.message || "일괄 수정에 실패했습니다.");
      }

      alert(result.message || "수정이 완료되었습니다.");
      await fetchList();
    } catch (error) {
      setErrorMessage(error.message || "일괄 수정 실패");
    } finally {
      setApplyLoading(false);
    }
  };

  const previewColumns = useMemo(() => {
    if (!previewRows.length) {
      return [];
    }
    const preferred = ["uid", "symbol", "trade_start", "direction", "both_mode"];
    const dynamic = [
      ...filters.filter((row) => row.column).map((row) => row.column),
      ...updates.filter((row) => row.column).map((row) => row.column),
    ];
    const ordered = [...new Set([...preferred, ...dynamic])];
    return ordered.filter((column) => column in previewRows[0] && column !== "id" && column !== "restart");
  }, [previewRows, filters, updates]);

  const allLoadedSelected =
    previewRows.length > 0 &&
    previewRows.every((row) => selectedRowIds.has(Number(row.id)));

  return (
    <main className="admin-dashboard admin-bulk">
      <div className="admin-dashboard__glare" aria-hidden="true" />
      <section className="admin-dashboard__panel admin-bulk__panel">
        <header className="admin-dashboard__hero">
          <div>
            <p className="admin-dashboard__eyebrow">Bitsoop control center</p>
            <h1 className="admin-dashboard__title">{HOLD_BULK_VERSION_LABEL} DB 일괄 수정</h1>
            <p className="admin-bulk__subtitle">
              조건으로 목록을 조회한 뒤, 체크한 행만 SetpageHold 용어로 수정합니다.
            </p>
          </div>
          <div className="admin-dashboard__hero-actions">
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={() => {
                window.location.href = "/Adminpage";
              }}
            >
              관리자 홈
            </button>
          </div>
        </header>

        {errorMessage && <div className="admin-bulk__error">{errorMessage}</div>}

        <section className="admin-bulk__section">
          <h2 className="admin-bulk__section-title">1. 조건</h2>
          <p className="admin-bulk__hint">항목과 값이 <strong>같음</strong>인 행만 조회합니다. (예: UID · 8304169884)</p>
          <div className="admin-bulk__stack">
            {filters.map((row, index) => (
              <div className="admin-bulk__condition-row" key={`filter-${index}`}>
                <FieldSelect
                  value={row.column}
                  onChange={(event) => updateFilterRow(index, "column", event.target.value)}
                  placeholder="조건 항목"
                  allowedColumns={allowedColumns}
                />
                <span className="admin-bulk__equals">=</span>
                <ValueEditor
                  column={row.column}
                  value={row.value}
                  onChange={(event) => updateFilterRow(index, "value", event.target.value)}
                  disabled={!row.column}
                  mode="filter"
                />
                <button
                  type="button"
                  className="admin-dashboard__btn admin-dashboard__btn--ghost"
                  onClick={() => {
                    resetListState();
                    setFilters((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
                  }}
                  disabled={filters.length <= 1}
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
          <div className="admin-bulk__row admin-bulk__inline-btn">
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={() => {
                resetListState();
                setFilters((prev) => [...prev, emptyFilter()]);
              }}
            >
              조건 추가
            </button>
            <label className="admin-bulk__limit">
              목록 표시
              <input
                className="admin-bulk__input admin-bulk__input--small"
                type="number"
                min="1"
                max={LIST_LIMIT_MAX}
                value={listLimit}
                onChange={(event) => setListLimit(Math.min(Number(event.target.value) || LIST_LIMIT_DEFAULT, LIST_LIMIT_MAX))}
              />
              건
            </label>
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--ghost"
              onClick={handleLoadList}
              disabled={listLoading}
            >
              {listLoading ? "조회 중..." : "목록 조회"}
            </button>
          </div>
        </section>

        {previewRows.length > 0 && (
          <section className="admin-bulk__section">
            <div className="admin-bulk__section-head">
              <h2 className="admin-bulk__section-title">2. 대상 선택</h2>
              <div className="admin-bulk__row">
                <button type="button" className="admin-dashboard__btn admin-dashboard__btn--ghost" onClick={selectAllLoadedRows}>
                  목록 전체 선택
                </button>
                <button type="button" className="admin-dashboard__btn admin-dashboard__btn--ghost" onClick={clearRowSelection}>
                  선택 해제
                </button>
              </div>
            </div>
            <p className="admin-bulk__hint">
              조건 일치 {matchedCount?.toLocaleString()}건 중 {loadedCount.toLocaleString()}건 표시 · 선택{" "}
              <strong>{selectedRowIds.size.toLocaleString()}</strong>건
              {listTruncated && " · 일부만 표시 중"}
            </p>
            {listTruncated && matchedCount <= LIST_LIMIT_MAX && (
              <button
                type="button"
                className="admin-dashboard__btn admin-dashboard__btn--ghost admin-bulk__inline-btn"
                onClick={handleLoadAllMatched}
                disabled={listLoading}
              >
                조건 일치 전체 {Math.min(matchedCount, LIST_LIMIT_MAX).toLocaleString()}건 불러오기
              </button>
            )}
            {listTruncated && matchedCount > LIST_LIMIT_MAX && (
              <p className="admin-bulk__hint admin-bulk__hint--warn">
                조건 일치 {matchedCount.toLocaleString()}건입니다. 5,000건 이하가 되도록 조건을 더 좁혀주세요.
              </p>
            )}
            <div className="admin-dashboard__table-wrapper admin-bulk__table-wrapper">
              <table className="admin-dashboard__table admin-bulk__table">
                <thead>
                  <tr>
                    <th className="admin-bulk__check-col">
                      <input
                        type="checkbox"
                        checked={allLoadedSelected}
                        onChange={(event) => {
                          if (event.target.checked) {
                            selectAllLoadedRows();
                          } else {
                            clearRowSelection();
                          }
                        }}
                        aria-label="목록 전체 선택"
                      />
                    </th>
                    {previewColumns.map((columnName) => (
                      <th key={columnName}>
                        <span className="admin-bulk__th-label">{getHoldFieldLabel(columnName)}</span>
                        <span className="admin-bulk__th-column">{columnName}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => {
                    const rowId = Number(row.id);
                    const checked = selectedRowIds.has(rowId);
                    return (
                      <tr key={`preview-row-${rowId}`} className={checked ? "admin-bulk__row--selected" : ""}>
                        <td className="admin-bulk__check-col">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRowSelection(rowId)}
                            aria-label={`${row.uid ?? ""} ${row.symbol ?? ""} 선택`.trim() || "행 선택"}
                          />
                        </td>
                        {previewColumns.map((columnName) => (
                          <td key={`${rowId}-${columnName}`} title={String(row[columnName] ?? "")}>
                            {formatHoldDisplayValue(columnName, row[columnName])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="admin-bulk__section">
          <h2 className="admin-bulk__section-title">3. 수정</h2>
          <p className="admin-bulk__hint">
            선택한 행에 적용할 값을 SetpageHold 와 동일하게 지정하세요.
            탈출 최고가/최저가 갱신은 홀드 설정 화면 버튼과 같이 기준가를 0으로 초기화합니다.
          </p>
          <div className="admin-bulk__stack">
            {updates.map((row, index) => (
              <div className="admin-bulk__condition-row" key={`update-${index}`}>
                <FieldSelect
                  value={row.column}
                  onChange={(event) => updateSetRow(index, "column", event.target.value)}
                  placeholder="수정 항목"
                  allowedColumns={allowedColumns}
                />
                <ValueEditor
                  column={row.column}
                  value={row.value}
                  onChange={(event) => updateSetRow(index, "value", event.target.value)}
                  disabled={!row.column}
                />
                <button
                  type="button"
                  className="admin-dashboard__btn admin-dashboard__btn--ghost"
                  onClick={() => setUpdates((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))}
                  disabled={updates.length <= 1}
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="admin-dashboard__btn admin-dashboard__btn--ghost admin-bulk__inline-btn"
            onClick={() => setUpdates((prev) => [...prev, emptyUpdate()])}
          >
            수정 항목 추가
          </button>
        </section>

        <section className="admin-bulk__section admin-bulk__actions">
          <button
            type="button"
            className="admin-dashboard__btn admin-dashboard__btn--danger"
            onClick={handleApply}
            disabled={applyLoading || selectedRowIds.size === 0}
          >
            {applyLoading ? "적용 중..." : `선택 ${selectedRowIds.size.toLocaleString()}건 수정 적용`}
          </button>
          {selectedRowIds.size === 0 && previewRows.length > 0 && (
            <span className="admin-bulk__hint">수정할 행을 체크해주세요.</span>
          )}
        </section>
      </section>
    </main>
  );
}
